import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Upload, FileText, Loader2, Award, Quote, ShieldCheck, Factory, Info, Camera } from "lucide-react";

const field = "w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-navy";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const assetUrl = (u) => (u && u.startsWith("/api/") ? BACKEND + u : u);

// Upload a file to the public branding asset store, returns {url,name,type}
const uploadAsset = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/branding/asset", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
};

const Section = ({ icon: Icon, title, subtitle, children, onAdd, addLabel }) => (
  <div className="mb-card p-5">
    <div className="flex items-start justify-between mb-4">
      <div>
        <h3 className="font-display font-bold uppercase text-navy flex items-center gap-2"><Icon className="w-4 h-4" /> {title}</h3>
        {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
      </div>
      {onAdd && <button data-testid={`add-${title.toLowerCase().replace(/\s+/g, "-")}`} onClick={onAdd} className="text-xs font-semibold text-navy flex items-center gap-1 hover:text-brandred shrink-0"><Plus className="w-3.5 h-3.5" /> {addLabel || "Add"}</button>}
    </div>
    {children}
  </div>
);

const FileButton = ({ onUploaded, accept = "image/*,application/pdf", label = "Upload", testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <input ref={ref} type="file" accept={accept} className="hidden" data-testid={testid}
        onChange={async (e) => {
          const file = e.target.files?.[0]; if (!file) return;
          if (file.size > 25 * 1024 * 1024) return toast.error("File must be under 25 MB");
          setBusy(true);
          try { const d = await uploadAsset(file); onUploaded(d); toast.success("Uploaded"); }
          catch { toast.error("Upload failed"); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
        }} />
      <button onClick={() => ref.current?.click()} disabled={busy}
        className="text-xs font-semibold border-2 border-slate-200 rounded-lg px-2.5 py-1.5 text-navy hover:border-navy flex items-center gap-1 disabled:opacity-60">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {label}
      </button>
    </>
  );
};

const DocChip = ({ doc, onRemove }) => (
  <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5" data-testid="doc-chip">
    {doc.type === "pdf" ? <FileText className="w-4 h-4 text-brandred shrink-0" /> :
      <img src={assetUrl(doc.url)} alt="" className="w-6 h-6 rounded object-cover shrink-0" />}
    <a href={assetUrl(doc.url)} target="_blank" rel="noreferrer" className="text-xs text-navy truncate max-w-[140px] hover:underline">{doc.name || "file"}</a>
    <button onClick={onRemove} className="text-slate-400 hover:text-brandred"><Trash2 className="w-3.5 h-3.5" /></button>
  </div>
);

export default function BrandingContent({ f, set }) {
  const arr = (k) => Array.isArray(f[k]) ? f[k] : [];
  const upd = (k, i, patch) => set(k, arr(k).map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const add = (k, item) => set(k, [...arr(k), item]);
  const del = (k, i) => set(k, arr(k).filter((_, idx) => idx !== i));

  return (
    <div className="space-y-6 mt-6">
      {/* ABOUT US */}
      <Section icon={Info} title="About Us" subtitle="Shown on the proposal introduction page.">
        <label className="text-xs font-semibold uppercase text-slate-500">Company Story</label>
        <textarea data-testid="brand-about_us" rows={4} value={f.about_us || ""} onChange={(e) => set("about_us", e.target.value)} className={`${field} mt-1`} />
        <label className="text-xs font-semibold uppercase text-slate-500 mt-3 block">Owner Message</label>
        <textarea data-testid="brand-about_owner" rows={3} value={f.about_owner || ""} onChange={(e) => set("about_owner", e.target.value)} className={`${field} mt-1`} />
      </Section>

      {/* TESTIMONIALS */}
      <Section icon={Quote} title="Testimonials" subtitle="Customer reviews that build trust." onAdd={() => add("testimonials", { name: "", text: "" })} addLabel="Add review">
        <div className="space-y-3">
          {arr("testimonials").map((t, i) => (
            <div key={i} className="border border-slate-200 rounded-xl p-3" data-testid={`testimonial-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <input value={t.name || ""} onChange={(e) => upd("testimonials", i, { name: e.target.value })} placeholder="Reviewer name" className="flex-1 border-2 border-slate-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-navy" />
                <button onClick={() => del("testimonials", i)} className="text-slate-400 hover:text-brandred"><Trash2 className="w-4 h-4" /></button>
              </div>
              <textarea value={t.text || ""} onChange={(e) => upd("testimonials", i, { text: e.target.value })} rows={2} placeholder="Review text" className={field} />
            </div>
          ))}
          {!arr("testimonials").length && <p className="text-sm text-slate-400">No testimonials yet.</p>}
        </div>
      </Section>

      {/* AWARDS */}
      <Section icon={Award} title="Awards & Certifications" subtitle="Badges/logos shown as social proof." onAdd={() => add("awards", { label: "", image_url: "" })} addLabel="Add badge">
        <div className="grid sm:grid-cols-2 gap-3">
          {arr("awards").map((a, i) => (
            <div key={i} className="flex items-center gap-2 border border-slate-200 rounded-xl p-2" data-testid={`award-${i}`}>
              <div className="w-12 h-12 rounded-lg bg-slate-50 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                {a.image_url ? <img src={assetUrl(a.image_url)} alt="" className="w-full h-full object-contain" /> : <Award className="w-5 h-5 text-slate-300" />}
              </div>
              <input value={a.label || ""} onChange={(e) => upd("awards", i, { label: e.target.value })} placeholder="Badge label" className="flex-1 border-2 border-slate-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-navy" />
              <FileButton testid={`award-file-${i}`} accept="image/*" label="Logo" onUploaded={(d) => upd("awards", i, { image_url: d.url })} />
              <button onClick={() => del("awards", i)} className="text-slate-400 hover:text-brandred"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </Section>

      {/* WARRANTY DOCS */}
      <Section icon={ShieldCheck} title="Warranty Documents" subtitle="PDFs or images homeowners can reference.">
        <div className="flex flex-wrap gap-2 items-center">
          {arr("warranty_docs").map((d, i) => (
            <DocChip key={i} doc={d} onRemove={() => del("warranty_docs", i)} />
          ))}
          <FileButton testid="warranty-doc-file" label="Add document" onUploaded={(d) => add("warranty_docs", { name: d.name, url: d.url, type: d.type })} />
        </div>
      </Section>

      {/* TERMS */}
      <Section icon={FileText} title="Terms & Conditions" subtitle="Editable sections printed on every proposal." onAdd={() => add("terms", { title: "", body: "" })} addLabel="Add section">
        <div className="space-y-3">
          {arr("terms").map((t, i) => (
            <div key={i} className="border border-slate-200 rounded-xl p-3" data-testid={`term-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-bold text-slate-400">{i + 1}.</span>
                <input value={t.title || ""} onChange={(e) => upd("terms", i, { title: e.target.value })} placeholder="Section title" className="flex-1 border-2 border-slate-200 rounded-lg px-2 py-1.5 text-sm font-semibold outline-none focus:border-navy" />
                <button onClick={() => del("terms", i)} className="text-slate-400 hover:text-brandred"><Trash2 className="w-4 h-4" /></button>
              </div>
              <textarea value={t.body || ""} onChange={(e) => upd("terms", i, { body: e.target.value })} rows={3} placeholder="Section text ({company} is replaced with your company name)" className={field} />
            </div>
          ))}
        </div>
      </Section>

      {/* MANUFACTURERS */}
      <Section icon={Factory} title="Manufacturers" subtitle="Sales sheets & color catalogs used in the proposal builder." onAdd={() => add("manufacturers", { name: "", logo_url: "", sales_sheets: [], color_catalogs: [] })} addLabel="Add manufacturer">
        <div className="space-y-4">
          {arr("manufacturers").map((m, i) => (
            <div key={i} className="border border-slate-200 rounded-xl p-4" data-testid={`manufacturer-${i}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-lg bg-slate-50 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                  {m.logo_url ? <img src={assetUrl(m.logo_url)} alt="" className="w-full h-full object-contain" /> : <Factory className="w-5 h-5 text-slate-300" />}
                </div>
                <input value={m.name || ""} onChange={(e) => upd("manufacturers", i, { name: e.target.value })} placeholder="Manufacturer name" className="flex-1 border-2 border-slate-200 rounded-lg px-2 py-1.5 text-sm font-semibold outline-none focus:border-navy" />
                <FileButton testid={`mfr-logo-${i}`} accept="image/*" label="Logo" onUploaded={(d) => upd("manufacturers", i, { logo_url: d.url })} />
                <button onClick={() => del("manufacturers", i)} className="text-slate-400 hover:text-brandred"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-[11px] font-semibold uppercase text-slate-500 mb-1.5">Sales Sheets</div>
                  <div className="flex flex-wrap gap-2 items-center">
                    {(m.sales_sheets || []).map((d, j) => (
                      <DocChip key={j} doc={d} onRemove={() => upd("manufacturers", i, { sales_sheets: m.sales_sheets.filter((_, k) => k !== j) })} />
                    ))}
                    <FileButton testid={`mfr-sheet-${i}`} label="Add" onUploaded={(d) => upd("manufacturers", i, { sales_sheets: [...(m.sales_sheets || []), { name: d.name, url: d.url, type: d.type }] })} />
                  </div>
                </div>
                <div>
                  <div className="text-[11px] font-semibold uppercase text-slate-500 mb-1.5">Color Catalogs</div>
                  <div className="flex flex-wrap gap-2 items-center">
                    {(m.color_catalogs || []).map((d, j) => (
                      <DocChip key={j} doc={d} onRemove={() => upd("manufacturers", i, { color_catalogs: m.color_catalogs.filter((_, k) => k !== j) })} />
                    ))}
                    <FileButton testid={`mfr-catalog-${i}`} label="Add" onUploaded={(d) => upd("manufacturers", i, { color_catalogs: [...(m.color_catalogs || []), { name: d.name, url: d.url, type: d.type }] })} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* PROJECT PHOTO LIBRARY */}
      <Section icon={Camera} title="Project Photo Library" subtitle="Reusable before/after job photos you can drop into any proposal.">
        <div className="grid sm:grid-cols-3 gap-3">
          {arr("photo_library").map((ph, i) => (
            <div key={i} className="border border-slate-200 rounded-xl p-2" data-testid={`libphoto-${i}`}>
              <div className="w-full h-28 rounded-lg bg-slate-50 border border-slate-200 overflow-hidden mb-2">
                {ph.url ? <img src={assetUrl(ph.url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Camera className="w-5 h-5 text-slate-300" /></div>}
              </div>
              <div className="flex items-center gap-1">
                <input value={ph.caption || ""} onChange={(e) => upd("photo_library", i, { caption: e.target.value })} placeholder="Caption" className="flex-1 border-2 border-slate-200 rounded-lg px-2 py-1 text-xs outline-none focus:border-navy" />
                <button onClick={() => del("photo_library", i)} className="text-slate-400 hover:text-brandred"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3"><FileButton testid="lib-photo-file" accept="image/*" label="Add photo" onUploaded={(d) => add("photo_library", { url: d.url, caption: "" })} /></div>
      </Section>
    </div>
  );
}
